import Minze, { EventListeners, MinzeElement, Reactive } from 'minze'
import { Font, MODALS, SCREEN_WIDTHS } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { GEOLOCATION_INDEX } from '../../../modules/geo'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import {
  AbstractSearchCore,
  AbstractSubscriber,
  IFacet,
  ISearchConfig,
} from '../../../modules/search'
import { formatToponym } from '../../../modules/toponym'
import { getVisibleWidth } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'regionSearch', {
  currentLocation: 'Localisation automatique',
  searchPlaceholderCity: 'Rechercher une ville',
  searchPlaceholderCounty: 'Rechercher une région',
  clearFilter: 'Effacer',
  where: 'Où',
  cityLabel: 'Ville',
  regionLabel: 'Région',
  label: 'Ville ou région',
  empty: 'Filtrez par région',
  mobileLabel: 'Ville/Région',
  applyButton: 'Appliquer',
  noMatchError: 'Aucune ville correspondante',
})

Locale.getInstance().registerKeySet('en', 'regionSearch', {
  currentLocation: 'My location',
  searchPlaceholderCity: 'Search for a city',
  searchPlaceholderCounty: 'Search for a region',
  clearFilter: 'Erase',
  where: 'Where',
  cityLabel: 'City',
  regionLabel: 'Region',
  label: 'City or region',
  empty: 'Filter by region',
  mobileLabel: 'City/Region',
  applyButton: 'Apply',
  noMatchError: 'No city matches your search',
})

/*

When this component was first created, it was using the "region" name everywhere.
However now you can search for cities or for regions. To not waste time renaming everything,
Whatever variable or functions with "Region" inside is about cities while the actual regions are named counties/county in the code.

TODO : Rename everything to be consistent with the new naming convention. (cities/regions)

*/
export interface LvcRegionSearch {
  selectedRanges: string[]
  //  searchMode: string;
}

export class LvcRegionSubscriber extends AbstractSubscriber {
  _core: AbstractSearchCore | null = null
  regions: any[] = []
  updateCallback: (data: any, searchMode: string) => void

  constructor(groupId: string, updateCallback: (data: any, searchMode: string) => void) {
    super(groupId)
    this.updateCallback = updateCallback
  }

  async attached(core: AbstractSearchCore): Promise<void> {
    this._core = core
  }

  detaching() {}

  public async setRegions(regions: any[], selectedMode: string): Promise<void> {
    let searchConfig: ISearchConfig = this._core?.searchConfig ?? {}
    let indexesWithGeo = this._core?.getIndexesWithGeo() ?? []
    let options = searchConfig.options ?? {}
    let aroundLatLng = options.aroundLatLng ?? {}
    let region = regions[0] ?? null

    // selectedMode can be "regions" or "counties".
    // if regions, we will use _geoloc and aroundLatLng
    // if counties, we will use "touristRegion" facet and remove aroundLatLng

    if (selectedMode === 'regions') {
      if (region) {
        indexesWithGeo.forEach(index => {
          aroundLatLng[index] = `${region.latitude},${region.longitude}`
        })

        searchConfig.options = options
        searchConfig.options.aroundLatLng = aroundLatLng
        searchConfig.locationLabel = region.name
      } else {
        indexesWithGeo.forEach(index => {
          delete aroundLatLng[index]
        })
      }

      // If touristRegion exists, ensure it gets removed prior to executing search.
      let facetIndex = searchConfig.facets?.findIndex(
        (facet: IFacet) => facet.name === 'touristRegion'
      )

      if (facetIndex !== undefined && facetIndex !== -1) {
        searchConfig.facets?.splice(facetIndex, 1)
      }
    } else {
      let originalFacet: IFacet | undefined = searchConfig.facets?.find(
        (facet: IFacet) => facet.name === 'touristRegion'
      )
      if (originalFacet) {
        originalFacet.options = []
      } else {
        originalFacet = {
          name: 'touristRegion',
          options: [],
        }

        searchConfig.facets?.push(originalFacet)
      }
      if (region) {
        originalFacet.options.push({
          value: region.county,
          selected: true,
          available: true,
          extraData: region,
        })
      }
      // Removing GEOLOCATION for "county" / "touristRegion" search.
      searchConfig.options = options
      searchConfig.options.aroundLatLng = {}
      searchConfig.locationLabel = ''
    }

    if (searchConfig) {
      await this._core?.applySearchConfig(searchConfig, true)
    }
  }

  public async getRegions(ids: string[]): Promise<any[]> {
    let core = this._core
    if (!core) {
      return []
    }

    let regions = await core.getObjects(GEOLOCATION_INDEX, ids)
    return regions
  }

  public async getCounties(ids: string[]): Promise<any[]> {
    let core = this._core
    if (!core) {
      return []
    }

    const geo = MainModule.getInstance().getGeo()
    let counties = geo ? await geo.getAllCounties() : []

    return counties
  }

  public async onSearchConfigUpdate(data: any): Promise<void> {
    let indexesWithGeo = this._core?.getIndexesWithGeo() ?? []
    let options = data.options ?? {}
    let aroundLatLng = options.aroundLatLng ?? {}

    let facet = data.facets?.find((facet: IFacet) => facet.name === 'touristRegion')

    if (facet && facet.options.length > 0) {
      let selectedCounty = facet.options[0]
      let county = selectedCounty.extraData
      if (county) {
        this.updateCallback(county, 'counties')
        return
      }
    } else {
      if (indexesWithGeo.length > 0) {
        let latLng = aroundLatLng[indexesWithGeo[0]] ?? null
        if (latLng) {
          let latLngParts = latLng.split(',')
          let latitude = parseFloat(latLngParts[0])
          let longitude = parseFloat(latLngParts[1])
          const geo = MainModule.getInstance().getGeo()
          let loc = geo ? await geo.getNearestLocation(latitude, longitude, 5000) : null

          this.updateCallback(loc, 'regions')
          if (loc && loc.name && typeof loc.name === 'string') {
            Minze.cast('lvc:search-geolocation-update', {
              locationLabel: loc.name as string,
            })
          }

          return
        }
      }
    }
    this.updateCallback(null, 'regions')
  }

  async onSearchTextUpdate(): Promise<void> {}

  async onFacetUpdate(): Promise<void> {}

  async onResultUpdate(): Promise<void> {}

  public isURLQuery(): boolean {
    const url = new URL(window.location.href)
    const search = url.searchParams.get('query')

    return search !== null
  }

  public async triggerLocationUpdate(): Promise<void> {
    await this.onSearchConfigUpdate(this._core?.searchConfig ?? {})
  }

  public async waitUntilReady(): Promise<void> {
    return new Promise(resolve => {
      let interval = setInterval(() => {
        if (this._core) {
          clearInterval(interval)
          resolve()
        }
      }, 10)
    })
  }
}

export class LvcRegionSearch extends MinzeElement {
  private regions: any[] = []
  private selectedRegions: any[] = []
  private counties: any[] = []
  private selectedCounties: any[] = []
  private selectedMode: string = 'regions'
  private lvcRegionSubscriber: LvcRegionSubscriber | null = null
  private currentLocationSelected = false
  private clickCb = Callback.attach(this.onButtonClick.bind(this))
  private clearCb = Callback.attach(this.onClearClick.bind(this))
  private toggleCb = Callback.attach(this.onDropdownToggle.bind(this))
  private drawerToggleCb = Callback.attach(this.onDrawerToggle.bind(this))
  private drawerClickCb = Callback.attach(this.onDrawerToggle.bind(this))
  private isOpen: boolean = false
  private searchMode: string = 'regions'
  private defaultLocation: any = null
  private debounceTimer: NodeJS.Timeout | null = null

  reactive: Reactive = [
    ['selectedRanges', []],
    ['profileRadius', null],
  ]

  onButtonClick(): void {
    MainModule.getInstance().showModal(MODALS.REGION_DROPDOWN)
  }

  onClearClick(): void {
    this.clearFilter(new Event('click'))
  }

  onDropdownToggle(state: boolean): void {
    this.isOpen = state
    this.rerender()
  }

  onDrawerToggle(): void {
    this.isOpen = !this.isOpen
    this.rerender()
  }

  openCitySearch = () => {
    this.searchMode = 'regions'
    this.rerender()
  }

  openRegionSearch = () => {
    this.searchMode = 'counties'
    this.rerender()
  }

  openRegionDropdown = (event: Event) => {
    let target = event.target as HTMLElement

    if (target?.tagName === 'LVC-CLEAR-FILTER-BUTTON') return
    const mainModule = MainModule.getInstance()
    mainModule.showModal(MODALS.REGION_DROPDOWN)
  }

  clearFilter = (event?: Event) => {
    if (event) event.preventDefault()
    this.selectedRanges = []
    this.selectedRegions = []
    this.selectedCounties = []

    this.lvcRegionSubscriber?.setRegions(this.selectedRegions, this.selectedMode)
    this.rerender()
    MainModule.getInstance().hideModal(MODALS.REGION_DROPDOWN)
  }

  async handleSearchUpdate(data: any, searchMode: string): Promise<void> {
    if (searchMode === 'regions') {
      this.selectedMode = 'regions'
      this.searchMode = 'regions'

      if (data) {
        this.selectedRanges = []
        this.selectedRegions = []
        this.selectedCounties = []

        if (!this.selectedRanges.includes(data.id)) {
          this.selectedRanges.push(data.id)
          this.selectedRegions.push(data)
        }
      } else {
        if (MainModule.getInstance().getRouting().currentPageHasQueryString()) {
          this.selectedRanges = []
          this.selectedRegions = []
        }
      }

      await this.updateRegions(this.selectedRegions)
      this.rerender()
    } else {
      this.selectedMode = 'counties'
      this.searchMode = 'counties'

      if (data) {
        this.selectedRanges = []
        this.selectedCounties = []
        this.selectedRegions = []

        if (!this.selectedRanges.includes(data.id)) {
          this.selectedRanges.push(data.id)
          this.selectedCounties.push(data)
        }
      }

      await this.updateCounties(this.selectedCounties)
      this.rerender()
    }
  }

  async applyLocation(location: any) {
    if (!location) return

    const isRegionEmpty = this.selectedRegions?.length <= 0
    const isCountyEmpty = this.selectedCounties?.length <= 0
    const isFilterEmpty = isRegionEmpty && isCountyEmpty
    const isDefaultLocationApplied =
      isCountyEmpty &&
      this.selectedRegions?.length === 1 &&
      this.defaultLocation?.id === this.selectedRegions?.[0]?.id

    if (isFilterEmpty || isDefaultLocationApplied) {
      await this.updateRegions([location])
      await this.lvcRegionSubscriber?.setRegions(this.selectedRegions, 'regions')
      this.defaultLocation = location
    }
  }

  async handleAuthChanged(event: Event) {
    const detail = (event as CustomEvent<any>).detail

    const userData = detail.user
    if (userData) {
      const mainModule = MainModule.getInstance()

      let ud = mainModule.getAuth().getUserData()
      const personalInfo = ud.profile.personalData

      // the user can set his preferred radius on his profile
      // check if we have a value there and set it as the selected radius
      if (personalInfo.radius) {
        this.profileRadius = personalInfo.radius
      }

      if (personalInfo.zipcode) {
        const geo = mainModule.getGeo()
        if (geo) {
          const location = await geo.getNearestLocationByPostalCode(personalInfo.zipcode)
          geo.currentLocation = location
        }
        this.rerender()
      }
      this.rerender()
    }
  }

  async onReady(): Promise<void> {
    this.lvcRegionSubscriber = new LvcRegionSubscriber(
      this.dataGroupId as string,
      this.handleSearchUpdate.bind(this)
    )
    MainModule.getInstance().getSearchManager().subscribe(this.lvcRegionSubscriber)
    await this.lvcRegionSubscriber.waitUntilReady()
    await this.lvcRegionSubscriber.triggerLocationUpdate()
    let geo = MainModule.getInstance().getGeo()

    const mainModule = MainModule.getInstance()
    const isSearchPage = mainModule.getRouting().getRouteKeyFromCurrentLocation() == 'searchResults'

    if (!isSearchPage && geo) {
      await this.applyLocation(geo.currentLocation)
    }

    // same thing here woohoo
    MainModule.getInstance()
      .getEE()
      .on('geo:location', async location => {
        if (!isSearchPage) {
          await this.applyLocation(location)
        }
      })

    let regions = geo ? await geo.listNearMe(15000) : []
    let counties = geo ? await geo.getAllCounties() : []

    await this.populateRegions(regions)
    await this.populateCounties(counties)
    this.rerender()
  }

  async onSearchRegions(event: Event): Promise<void> {
    const target = event.currentTarget as HTMLInputElement
    const value = target.value

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.debounceTimer = setTimeout(async () => {
      const geo = MainModule.getInstance().getGeo()
      const regions = geo ? await geo.list(value) : []

      await this.populateRegions(regions)
      this.rerender()
    }, 500)
  }

  async onSearchCounties(event: Event): Promise<void> {
    const target = event.currentTarget as HTMLInputElement
    const value = target.value

    const geo = MainModule.getInstance().getGeo()
    const counties = geo ? await geo.listCounties(value) : [] // New function, targeting "cities_county_asc" replica

    await this.populateCounties(counties)
    this.rerender()
  }

  async handleSearchCountiesInputSubmitted() {
    const countyId = this.counties?.[0]?.id
    if (countyId) {
      await this.selectCounty(countyId)
    } else {
      const mainModule = MainModule.getInstance()
      mainModule.showToastAlert(Locale.getKey('regionSearch.noMatchError'), 3000, true)
    }
  }

  async handleSearchCountiesInputSubmit(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      await this.handleSearchCountiesInputSubmitted()
    }
  }

  handleSearchRegionsInputSubmit(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      Minze.cast('lvc:search-regions-input-submitted')
    }
  }

  async handleSearchRegionsInputSubmitted() {
    const regionId = this.regions?.[0]?.id
    if (regionId) {
      await this.selectRegion(regionId)
    } else {
      const mainModule = MainModule.getInstance()
      mainModule.showToastAlert(Locale.getKey('regionSearch.noMatchError'), 3000, true)
    }
  }

  onSearchCountiesInputFocus() {
    window.addEventListener('keypress', this.handleSearchCountiesInputSubmit.bind(this))
  }

  onSearchCountiesInputBlur() {
    window.removeEventListener('keypress', this.handleSearchCountiesInputSubmit.bind(this))
  }

  onSearchRegionsInputFocus() {
    window.addEventListener('keypress', this.handleSearchRegionsInputSubmit)
  }

  onSearchRegionsInputBlur() {
    window.removeEventListener('keypress', this.handleSearchRegionsInputSubmit)
  }

  getRegionNameFromId(id: string): string {
    let region = this.regions.find(region => region.id === id)

    if (region) {
      return region.name
    }

    return ''
  }

  async populateRegions(regions: any[]) {
    this.regions = [...this.selectedRegions]

    regions.map(region => {
      let foundRegion = this.regions.find(item => {
        return item.id == region.id
      })
      if (!foundRegion) {
        this.regions.push(region)
      }
    })
    await this.updateRegions(this.selectedRegions)
  }

  async populateCounties(counties: any[]) {
    this.counties = counties // Changed logic so that the full list of counties is always displayed.

    counties.map(county => {
      let foundCounty = this.counties.find(item => {
        return item.id == county.id
      })
      if (!foundCounty) {
        this.counties.push(county)
      }
    })
    await this.updateCounties(this.selectedCounties)
  }

  async updateRegions(selectedRegions: any[]): Promise<void> {
    this.regions = this.regions.filter(region => {
      let isSelected = selectedRegions.find(item => item.id === region.id)

      if (isSelected) {
        return false
      }
      return true
    })
    this.selectedRegions = selectedRegions
    let currentLocation = MainModule.getInstance().getGeo().currentLocation
    let currentLocationIndex = currentLocation
      ? this.selectedRegions.findIndex(region => region.id === currentLocation.id)
      : -1

    this.currentLocationSelected = currentLocationIndex !== -1
    let compiledRegions = [...selectedRegions, ...this.regions]
    let nonPopulatedRegions = []
    for (let i = 0; i < compiledRegions.length; i++) {
      let region = compiledRegions[i]
      if (!region.name) {
        nonPopulatedRegions.push(region.id)
      }
    }

    let populatedRegions = await this.lvcRegionSubscriber?.getRegions(nonPopulatedRegions)

    populatedRegions?.map(region => {
      let index = compiledRegions.findIndex(item => item.id === region.id)
      compiledRegions[index] = region
    })

    this.regions = compiledRegions
    this.rerender()
  }

  // Commented out lots of code here, seems to work ok...
  async updateCounties(selectedCounties: any[]): Promise<void> {
    // this.counties = this.counties.filter((county) => {
    //   let isSelected = selectedCounties.find(
    //     (item) => item.id === county.id || item.county === county.county,
    //   );

    //   if (isSelected) {
    //     return false;
    //   }
    //   return true;
    // });
    this.selectedCounties = selectedCounties

    //let compiledCounties = [...selectedCounties, ...this.counties];
    // let compiledCounties = this.counties
    // let nonPopulatedCounties = [];
    // for (let i = 0; i < compiledCounties.length; i++) {
    //   let county = compiledCounties[i];
    //   if (!county.name) {
    //     compiledCounties.push(county.id);
    //   }
    // }

    // let populatedCounties =
    //   await this.lvcRegionSubscriber?.getCounties(compiledCounties);

    // populatedCounties?.map((county) => {
    //   let index = compiledCounties.findIndex((item) => item.id === county.id);
    //   compiledCounties[index] = county;
    // });

    // this.counties = compiledCounties;
    this.rerender()
  }

  async selectRegion(region: string) {
    const selectedRanges = [...this.selectedRanges]
    if (region && selectedRanges?.includes(region)) return this.clearFilter()
    const currentLocation = MainModule.getInstance().getGeo().currentLocation
    if (region === '__CURRENT_LOCATION__') {
      region = currentLocation?.id ?? ''
    }
    this.selectedRanges = [region as string]

    let newSelectedRegions: any[] = []
    newSelectedRegions = []
    this.selectedRanges.map(item => {
      let regionItem = this.regions.find(region => region.id === item)

      if (regionItem) {
        newSelectedRegions.push(regionItem)
      } else {
        if (item === currentLocation?.id) {
          newSelectedRegions.push(currentLocation)
        }
      }
    })

    await this.updateRegions(newSelectedRegions)
    this.lvcRegionSubscriber?.setRegions(this.selectedRegions, this.selectedMode)
    this.isOpen = false
    this.rerender()
    MainModule.getInstance().hideModal(MODALS.REGION_DROPDOWN)
  }

  async selectCounty(county: string) {
    const selectedRanges = [...this.selectedRanges]
    if (county && selectedRanges?.includes(county)) return this.clearFilter()

    this.selectedRanges = [county as string]

    let newSelectedCounties: any[] = []
    this.selectedRanges.map(item => {
      let countyItem = this.counties.find(county => county.id === item)

      if (countyItem) {
        newSelectedCounties.push(countyItem)
      }
    })

    await this.updateCounties(newSelectedCounties)
    this.lvcRegionSubscriber?.setRegions(this.selectedCounties, 'counties')
    this.isOpen = false
    this.rerender()
    MainModule.getInstance().hideModal(MODALS.REGION_DROPDOWN)
  }

  async onSelectRegion(event: Event): Promise<void> {
    this.selectedMode = this.searchMode

    const target = event.currentTarget as HTMLElement

    this.selectedRanges.splice(0, this.selectedRanges.length)
    this.selectedCounties.splice(0, this.selectedCounties.length)
    this.selectedRegions.splice(0, this.selectedRegions.length)

    if (this.selectedMode === 'regions') {
      let region = target.getAttribute('data-region')
      if (region) await this.selectRegion(region)
    } else {
      // Handling the logic for "county" selections and filters
      let county = target.getAttribute('data-county')
      if (county) await this.selectCounty(county)
    }
  }

  renderOption = (label: string, id: string, lastItem?: boolean) => {
    const displayLabel = formatToponym(label)

    if (this.searchMode === 'regions') {
      return `
      <button class="region-option" data-region="${id}">
        <span>
          ${displayLabel}
        </span>
        ${this.selectedRanges.includes(id) && this.selectedMode === 'regions' ? `<lvc-circle-check-select-icon></lvc-circle-check-select-icon>` : ``}
      </button>
    `
    } else {
      return `
      <button class="county-option" data-county="${id}">
        <span>
          ${displayLabel}
        </span>
        ${this.selectedRanges.includes(id) && this.selectedMode === 'counties' ? `<lvc-circle-check-select-icon></lvc-circle-check-select-icon>` : ``}
      </button>
    `
    }
  }

  renderContent = () => {
    let currentLocationHtml = this.renderOption(
      Locale.getKey('regionSearch.currentLocation'),
      '__CURRENT_LOCATION__'
    )

    if (this.currentLocationSelected) {
      currentLocationHtml = ''
    }

    let selectedLabel =
      this.selectedMode === 'regions'
        ? this.selectedRegions[0]?.name
        : this.selectedCounties[0]?.county

    if (this.searchMode === 'regions') {
      return /* html */ `          
        
        <lvc-box width="100%" display="flex" rowGap="20px" flexDirection="column">  
          <lvc-box width="100%">
            <lvc-box display="flex" alignItems="center" height="69px" flexGrow="1" columnGap="10px"  borderBottom="1px solid rgba(30, 0, 125, 0.3)">
              <lvc-search-icon width="26" height="26" color="#1E007D"></lvc-search-icon>
              <input class="regions-search-input" placeholder="${Locale.getKey('regionSearch.searchPlaceholderCity')}"
                padding="unset" fontSize="16px" height="24px" />
            </lvc-box>
          </lvc-box>
          <lvc-box width="100%" display="flex" justifyContent="center">
            <lvc-box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
              <lvc-box display="flex" columnGap="4px" padding="4px" border="1px solid #1E007D" borderRadius="99px">
                <lvc-button enable-hover-box-shadow="true" theme="dark" width="162px" font-size="14px" font-weight="500" padding="12px" height="46px">
                  ${Locale.getKey('regionSearch.cityLabel')}
                </lvc-button>
                <lvc-button
                  width="162px"
                  height="46px"
                  theme="transparent"
                  flex="1"
                  border-radius="99px"
                  font-size="14px"
                  font-weight="500"
                  padding="12px"
                  id="btn-region-search"
                >
                  ${Locale.getKey('regionSearch.regionLabel')}
                </lvc-button>
              </lvc-box>
              <lvc-radius-search preferred-radius="${this.profileRadius}"></lvc-radius-search>
            </lvc-box>
          </lvc-box>


          <div class="city-container">
            ${currentLocationHtml}
            ${this.regions
              .map(
                (region, index) =>
                  `${this.renderOption(region.name, region.id, index + 1 >= this.regions.length)}`
              )
              .join('')}
          </div>
        </lvc-box>
        `
    } else if (this.searchMode === 'counties') {
      return /* html */ `      
        <lvc-box width="100%" display="flex" rowGap="20px" flexDirection="column">  
          <lvc-box borderBottom="1px solid rgba(30, 0, 125, 0.3)" width="100%">
            <lvc-box display="flex" alignItems="center" height="69px" flexGrow="1" columnGap="10px">
              <lvc-search-icon width="26" height="26" color="#1E007D"></lvc-search-icon>
              <input class="counties-search-input" placeholder="${Locale.getKey('regionSearch.searchPlaceholderCounty')}"
                padding="unset" fontSize="16px" height="24px" />
            </lvc-box>
          </lvc-box>

          <lvc-box width="100%" display="flex" justifyContent="center">
            <lvc-box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
              <lvc-box display="flex" columnGap="4px" padding="4px" border="1px solid #1E007D" borderRadius="99px">
                <lvc-button
                  width="162px"
                  height="46px"
                  theme="transparent"
                  flex="1"
                  border-radius="99px"
                  font-size="14px"
                  font-weight="500"
                  padding="12px"
                  id="btn-city-search">
                  ${Locale.getKey('regionSearch.cityLabel')}
                </lvc-button>
                <lvc-button enable-hover-box-shadow="true"
                  theme="dark" width="162px" font-size="14px" font-weight="500" padding="12px" height="46px">
                  ${Locale.getKey('regionSearch.regionLabel')}
                </lvc-button>
              </lvc-box>
            </lvc-box>
            <lvc-box mt="16px" class="calendar-container">
              <div type="text" id="my-calendar"></div>
            </lvc-box>
          </lvc-box>

          <div class="regions-container">
            ${this.counties
              .map(
                (county, index) =>
                  `${this.renderOption(county.county, county.id, index + 1 >= this.regions.length)}`
              )
              .join('')}
          </div>
        </lvc-box>
        `
    }
  }

  closeModal = () => {
    MainModule.getInstance().hideModal(MODALS.REGION_DROPDOWN)
  }

  getTextValue = () => {
    if (this.selectedMode === 'regions' && this.selectedRegions.length > 0) {
      const name = formatToponym(this.selectedRegions[0]?.name)
      return name.length > 15 ? `${name.slice(0, 15)}...` : name
    } else if (this.selectedMode === 'counties' && this.selectedCounties.length > 0) {
      const county = formatToponym(this.selectedCounties[0]?.county)
      return county.length > 15 ? `${county.slice(0, 15)}...` : county
    }
    return
  }

  renderDesktop() {
    return /* html */ `
    <lvc-box display="flex" alignItems="center" height="100%">
        <lvc-dropdown
          on-toggle="${this.toggleCb}"
          modal-id=${MODALS.REGION_DROPDOWN}
          flex-grow="1"
          fullscreen="true"
        >
          <lvc-search-filter-button
            slot="lvc-dropdown-trigger"
            class="desktop-region-search"
            preset="region-search"
            count="${this.selectedMode === 'regions' ? this.selectedRegions.length : this.selectedCounties.length}"
            ${this.isOpen ? 'selected' : ''}
            on-click="${this.clickCb}"
            on-clear="${this.clearCb}"
            data-first-child="true"
            data-value="${this.getTextValue()}"
          >
            ${Locale.getKey('regionSearch.label')}
          </lvc-search-filter-button>
          <div slot="lvc-dropdown-content" class="dropdown-content">
            ${this.renderContent()}
          </div>
        </lvc-dropdown>
      </lvc-box>
    `
  }

  renderMobile() {
    return /* html */ `
      <lvc-search-filter-mobile
        on-toggle="${this.drawerToggleCb}"
        title="${Locale.getKey('regionSearch.mobileLabel')}"
        data-count="${this.selectedMode === 'regions' ? this.selectedRegions.length : this.selectedCounties.length}"
        ${this.isOpen ? `open="true"` : ''}
      >
        <lvc-search-filter-button
          slot="lvc-drawer-trigger"
          preset="region-search-mobile"
          count="${this.selectedMode === 'regions' ? this.selectedRegions.length : this.selectedCounties.length}"
          ${this.isOpen ? 'selected' : ''}
          on-click="${this.drawerClickCb}"
          on-clear="${this.clearCb}"
          data-value="${this.getTextValue()}"
        >
          ${Locale.getKey('regionSearch.label')}
        </lvc-search-filter-button>
        <div slot="lvc-drawer-content" class="drawer-content">
          ${this.renderContent()}
        </div>
      </lvc-search-filter-mobile>
    `
  }

  html() {
    if (getVisibleWidth() < SCREEN_WIDTHS.DESKTOP_MEDIUM) {
      return this.renderMobile()
    }
    return this.renderDesktop()
  }

  css() {
    return /* css */ `
    lvc-radius-search {
      margin-top: 4px;
    }
    .regions-container, .city-container {
      max-height: 375px;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      font-family: inherit;
    }

    
    lvc-search-icon {
      display: flex;
    }

    .region-option, .county-option {
      display: flex;
      align-items: space-between;
      justify-content: space-between;
      padding: 10px 0;
      border: none;
      background: none;
      cursor: pointer;
      box-sizing: border-box;
      text-align: left;
      font-family: inherit;
    }

    .region-option, .county-option {
      transition: all 0.2s ease;
    }
    
    .region-option:hover, .county-option:hover {
      background: var(--brand-ciel, #BFF0FF);
      margin-left: 4px;
    }

    .region-option span, .county-option span {
      font-size: 14px;
      font-weight: ${Font.weight.medium};
      line-height: 150%;
      font-family: ${Font.family};
      color: var(--brand-minuit, #1E007D);
      line-height: 24px;
    }

    .counties-search-input, .regions-search-input {
      width: 100%;
      border: none;
      outline: none;
      font-size: 16px;
      background: transparent;
      font-weight: ${Font.weight.medium};
      line-height: 150%;
      color: #1E007D;
      font-family: ${Font.family}; 
    }

    h4 {
      margin: 0 0 4px 0;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      opacity: 0.3;
    }

    lvc-drawer {
      width: 100%;
    }

    lvc-drawer .regions-container {
      max-height: 250px;
    }

    lvc-drawer .search-input {
      border-radius: 50px;
      border: 1px solid var(--faded-minuit-30, rgba(30, 0, 125, 0.30));
      color: var(--brand-minuit, #1E007D);
      font-variant-numeric: lining-nums tabular-nums;
      font-feature-settings: 'clig' off, 'liga' off;

      /* D/textes/petits textes */
      font-family: ${Font.family};
      font-size: 14px;
      font-style: normal;
      font-weight: ${Font.weight.medium};
      line-height: 20px; /* 16.8px */
      padding: 10px 20px;

    }

    lvc-drawer .search-input:focus {
      border-radius: 50px;
      border: 1px solid var(--brand-minuit, #1E007D);
      background: var(--brand-ciel, #BFF0FF);
    }
    @media screen and (max-width: 991px) {
      lvc-radius-search {
        padding: 4px;
      }
    }
    @media screen and (max-width: 900px) {

      :host {
        padding: 0;
      }

      .regions-container {
      max-height: calc(100vh - 72px - 73px - 84.5px - 10px);
      }
    }
    `
  }

  onDestroy() {
    Callback.detach(this.clickCb)
    Callback.detach(this.clearCb)
    Callback.detach(this.toggleCb)
    Callback.detach(this.drawerToggleCb)
    Callback.detach(this.drawerClickCb)
  }

  eventListeners: EventListeners = [
    ['.dropdown-button-trigger', 'click', this.openRegionDropdown.bind(this)],
    ['lvc-clear-filter-button', 'click', this.clearFilter.bind(this)],
    ['.region-option', 'click', this.onSelectRegion.bind(this)],
    ['.county-option', 'click', this.onSelectRegion.bind(this)],
    ['.regions-search-input', 'input', this.onSearchRegions.bind(this)],
    ['.counties-search-input', 'input', this.onSearchCounties.bind(this)],
    ['.counties-search-input', 'focus', this.onSearchCountiesInputFocus.bind(this)],
    ['.counties-search-input', 'blur', this.onSearchCountiesInputBlur.bind(this)],
    ['.regions-search-input', 'focus', this.onSearchRegionsInputFocus.bind(this)],
    ['.regions-search-input', 'blur', this.onSearchRegionsInputBlur.bind(this)],
    [
      window,
      'lvc:search-regions-input-submitted',
      this.handleSearchRegionsInputSubmitted.bind(this),
    ],
    ['lvc-back-icon', 'click', this.closeModal.bind(this)],
    ['#btn-region-search', 'click', this.openRegionSearch.bind(this)],
    ['#btn-city-search', 'click', this.openCitySearch.bind(this)],
    [window, 'lvc:user_state_changed', this.handleAuthChanged.bind(this)],
  ]
}
